import { Link } from "@tanstack/react-router";
import { HugeiconsIcon } from "@hugeicons/react";
import { Building02Icon, LinkSquare02Icon } from "@hugeicons/core-free-icons";

import { SnipeitLogo, StarlinkLogo } from "@/components/snipe-it/SnipeitBrand";
import { cn } from "@/lib/utils";

/**
 * A value that names a record, as a compact link to it (ui-guideline §5.2):
 * 28 px, neutral hover, above a row's stretched link. ProjectLink shares it.
 */
export const RECORD_LINK =
  "group/record relative z-10 inline-flex h-7 max-w-full min-w-0 items-center gap-1.5 rounded-md px-1.5 text-xs outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50";

/** The asset's page in Snipe-IT, in a new tab; the arrow appears on hover and focus. */
export function SnipeitAssetLink({
  asset,
  className,
  logo = true,
}: {
  asset: { assetTag: string; url: string; starlink?: boolean };
  className?: string;
  /** Hidden where the row already says it comes from Snipe-IT. */
  logo?: boolean;
}) {
  return (
    <a
      href={asset.url}
      target="_blank"
      rel="noreferrer"
      title={`Ouvrir ${asset.assetTag} dans Snipe-IT`}
      aria-label={`Ouvrir ${asset.assetTag} dans Snipe-IT`}
      className={cn(RECORD_LINK, className)}
    >
      {logo && (asset.starlink ? <StarlinkLogo className="w-3.5" /> : <SnipeitLogo />)}
      <span className="min-w-0 truncate font-mono">{asset.assetTag}</span>
      <HugeiconsIcon
        icon={LinkSquare02Icon}
        className="size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/record:opacity-100 group-focus-visible/record:opacity-100"
        aria-hidden="true"
      />
    </a>
  );
}

/** The site in the UniFi page, found by name. */
export function UnifiSiteLink({ name, className }: { name: string; className?: string }) {
  return (
    <Link
      to="/admin/integrations/unifi"
      search={{ q: name }}
      title={`Voir le site ${name} dans UniFi`}
      className={cn(RECORD_LINK, className)}
    >
      <HugeiconsIcon
        icon={Building02Icon}
        className="size-3.5 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
      <span className="min-w-0 truncate">{name}</span>
    </Link>
  );
}
